import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Users, Plus, Search, Building2, Mail, UserCheck, UserX, Edit2, Loader2, Trash2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "", password: "", name: "", role: "worker", organization_id: ""
  });
  const [editFormData, setEditFormData] = useState({
    name: "", role: "", organization_id: "", is_active: true
  });

  useEffect(() => { fetchData(); }, []);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchData = async () => {
    const token = getToken();
    if (!token) { navigate("/admin/login"); return; }
    try {
      const [usersRes, orgsRes] = await Promise.all([
        fetch(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/organizations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (orgsRes.ok) setOrganizations(await orgsRes.json());
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success("Användare skapad!");
        setDialogOpen(false);
        setFormData({ email: "", password: "", name: "", role: "worker", organization_id: "" });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Kunde inte skapa användare");
      }
    } catch { toast.error("Ett fel uppstod"); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      const res = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(editFormData)
      });
      if (res.ok) {
        toast.success("Användare uppdaterad!");
        setEditDialogOpen(false);
        setSelectedUser(null);
        fetchData();
      } else {
        toast.error("Kunde inte uppdatera användare");
      }
    } catch { toast.error("Ett fel uppstod"); }
  };

  const handleDeactivate = async (user) => {
    if (!confirm(`Inaktivera ${user.name}?`)) return;
    try {
      const res = await fetch(`${API}/admin/users/${user.user_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        toast.success("Användare inaktiverad");
        fetchData();
      }
    } catch { toast.error("Ett fel uppstod"); }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || "",
      role: user.role || "worker",
      organization_id: user.organization_id || "",
      is_active: user.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const roleLabels = { admin: "Administratör", project_manager: "Projektledare", site_manager: "Platschef", worker: "Yrkesarbetare", supervisor: "Arbetsledare", subcontractor: "Underentreprenör" };
  const roleColors = { admin: "bg-purple-500/20 text-purple-400", project_manager: "bg-blue-500/20 text-blue-400", site_manager: "bg-cyan-500/20 text-cyan-400", worker: "bg-green-500/20 text-green-400", supervisor: "bg-amber-500/20 text-amber-400", subcontractor: "bg-slate-500/20 text-slate-400" };

  const orgMap = {};
  organizations.forEach(o => { orgMap[o.organization_id] = o.name; });

  const filteredUsers = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === "all" || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }} data-testid="admin-users-title">
            Användare
          </h1>
          <p className="text-slate-400 mt-1">{users.length} användare totalt</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="admin-create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Ny användare
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Skapa ny användare</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Namn *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-slate-700 border-slate-600 text-white" placeholder="Anna Andersson" data-testid="admin-user-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">E-post *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="bg-slate-700 border-slate-600 text-white" placeholder="anna@exempel.se" data-testid="admin-user-email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Lösenord *</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="bg-slate-700 border-slate-600 text-white" placeholder="Minst 6 tecken" data-testid="admin-user-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Roll</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="admin-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Organisation</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({ ...formData, organization_id: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="admin-user-org-select">
                    <SelectValue placeholder="Välj organisation" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {organizations.map((org) => (
                      <SelectItem key={org.organization_id} value={org.organization_id} className="text-white">{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" data-testid="admin-user-submit-btn">Skapa</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input placeholder="Sök användare..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="admin-user-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-white" data-testid="admin-user-role-filter">
            <SelectValue placeholder="Alla roller" />
          </SelectTrigger>
          <SelectContent className="bg-slate-700 border-slate-600">
            <SelectItem value="all" className="text-white">Alla roller</SelectItem>
            {Object.entries(roleLabels).map(([key, label]) => (
              <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users List */}
      <div className="grid gap-3">
        {filteredUsers.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">{search || roleFilter !== "all" ? "Inga användare hittades" : "Inga användare ännu"}</p>
            </CardContent>
          </Card>
        ) : (
          filteredUsers.map((user) => (
            <Card key={user.user_id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`admin-user-card-${user.user_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_active !== false ? 'bg-slate-600' : 'bg-slate-700'}`}>
                      <span className={`text-sm font-bold ${user.is_active !== false ? 'text-white' : 'text-slate-500'}`}>
                        {(user.name || user.email || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold ${user.is_active !== false ? 'text-white' : 'text-slate-500 line-through'}`}>{user.name || "Namnlös"}</h3>
                        <Badge className={roleColors[user.role] || "bg-slate-500/20 text-slate-400"}>
                          {roleLabels[user.role] || user.role}
                        </Badge>
                        {user.is_active === false && (
                          <Badge className="bg-red-500/20 text-red-400">Inaktiv</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {user.email}
                        </span>
                        {user.organization_name && (
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {user.organization_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={() => openEditDialog(user)} data-testid={`admin-edit-user-${user.user_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {user.is_active !== false && (
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDeactivate(user)} data-testid={`admin-deactivate-user-${user.user_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Redigera användare</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Namn</Label>
              <Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Roll</Label>
              <Select value={editFormData.role} onValueChange={(v) => setEditFormData({ ...editFormData, role: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {Object.entries(roleLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organisation</Label>
              <Select value={editFormData.organization_id} onValueChange={(v) => setEditFormData({ ...editFormData, organization_id: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Välj organisation" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {organizations.map((org) => (
                    <SelectItem key={org.organization_id} value={org.organization_id} className="text-white">{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={editFormData.is_active} onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })} className="rounded border-slate-600" />
              <Label htmlFor="is_active" className="text-slate-300">Aktiv</Label>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => setEditDialogOpen(false)}>Avbryt</Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600 text-black">Spara</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
